import { differenceInDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useViewport } from "src/contexts";
import { configurationsLandingPath, createCommunitiesUrl } from "src/RouteUrls";
import { useCurrentUser } from "src/utils/hooks/useCurrentUser";
import { Button, Icon, OptionPreviewImage } from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigStatus } from "~generated/graphqlTypes";
import { currencyFormatter, pluralize, queryResult, useTestIds } from "~utils";
import {
  HomeownerConfigurationsLandingPage_CheckoutConfigFragment,
  useHomeownerConfigurationsLandingPageQuery,
} from "./HomeownerConfigurationsLandingPage.generated";

type HomeownerConfigurationsLandingPageProps = {
  handleSignOut: () => Promise<void>;
};

export function HomeownerConfigurationsLandingPage({ handleSignOut }: HomeownerConfigurationsLandingPageProps) {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const query = useHomeownerConfigurationsLandingPageQuery({
    skip: !currentUser?.userId,
    variables: {
      filter: {
        status: [CheckoutConfigStatus.Draft, CheckoutConfigStatus.Reserved, CheckoutConfigStatus.Expired],
        userId: currentUser?.userId ? [currentUser.userId] : [],
      },
      page: { limit: 100 },
    },
  });

  if (!currentUser?.id) return null;

  return queryResult(query, ({ checkoutConfigs }) => {
    if (checkoutConfigs.entities.length === 0) {
      navigate(createCommunitiesUrl());
    }
    return (
      <HomeownerConfigurationsLandingPageView configurations={checkoutConfigs.entities} handleSignOut={handleSignOut} />
    );
  });
}

function HomeownerConfigurationsLandingPageView({
  configurations,
  handleSignOut,
}: {
  configurations: HomeownerConfigurationsLandingPage_CheckoutConfigFragment[];
  handleSignOut: () => Promise<void>;
}) {
  const { isMobile } = useViewport();

  const navigate = useNavigate();
  const tid = useTestIds({}, "homeownerConfigurationsLandingPage");

  function handleConfigurationClick(config: HomeownerConfigurationsLandingPage_CheckoutConfigFragment) {
    navigate(config.checkoutUrl?.path ?? "");
  }

  return (
    <>
      <div css={Css.z1.df.p2.jcc.aic.sticky.top0.bgWhite.ifTabletAndUp.jcsb.maxw75.mxa.$}>
        {!isMobile && (
          <div>
            <img width="153" height="26" src="/logo.svg" alt="Homebound" />
          </div>
        )}
        <div css={Css.t24.$}>Choose Configuration</div>
        {!isMobile && (
          <div>
            <Button
              btnFontSize="reg"
              size="medium"
              rounded
              onClick={() =>
                navigate(createCommunitiesUrl(), {
                  state: { from: configurationsLandingPath },
                })
              }
              {...tid.addConfigurationButtonDesktop}
            >
              + Configuration
            </Button>
          </div>
        )}
      </div>
      <div css={Css.tac.maxw75.my3.mxa.t14.gray.$}>
        You have {configurations.length} existing configurations associated with your email address.
      </div>
      <div css={Css.df.fdc.gap2.px3.ifTabletAndUp.maxw50.mxa.$}>
        {/* Iterate list of configurations with rendering, name, etc */}
        {configurations.map((config) => {
          const daysAgo = differenceInDays(new Date(), new Date(config.createdAt));
          return (
            <div
              key={config.id}
              css={{
                ...Css.df.gap1.$,
                ...Css.if(!isMobile).cursorPointer.onHover.bgGray100.$,
              }}
              onClick={!isMobile ? () => handleConfigurationClick(config) : undefined}
            >
              <OptionPreviewImage
                objectFit="cover"
                size={isMobile ? 80 : 160}
                optionName={config.name ?? "Configuration"}
                imageUrl={config.rendering?.largePreview ?? ""}
              />
              <div css={Css.df.fdc.gap1.f1.$}>
                <div css={Css.df.asStretch.jcsb.aic.$}>
                  <div css={Css.t16.$}>{config.name}</div>
                  <div css={Css.ifTabletAndUp.dn.$}>
                    <Button
                      btnFontSize="reg"
                      size="small"
                      variant="inline"
                      onClick={() => handleConfigurationClick(config)}
                    >
                      View
                    </Button>
                  </div>
                </div>
                <div css={Css.df.t14.gray600.$}>
                  {config.productOffering?.name}
                  <div css={Css.px1.$}>|</div>
                  {currencyFormatter(config.totalPriceInCents)}
                </div>
                <div css={Css.t12.gray600.mta.$}>
                  Created {daysAgo === 0 ? "today" : `${daysAgo} ${pluralize(daysAgo, "day")} ago`}
                </div>
              </div>
            </div>
          );
        })}
        {/* Desktop logout button is rendered inside the config list while mobile fixed to bototm */}
        {!isMobile && (
          <div css={Css.mt1.$}>
            <Button onClick={handleSignOut} size="small" variant={"secondary"}>
              Logout
              <Icon name="arrow-from-left" />
            </Button>
          </div>
        )}
      </div>
      {isMobile && (
        <div css={Css.df.fdc.aic.pt2.pb3.gap3.mt2.mx3.bt.bw2.bcTaupe.sticky.bottom0.bgWhite.$}>
          <div css={Css.t14.tac.$}>or</div>
          <div>
            <Button
              btnFontSize="reg"
              size="large"
              onClick={() =>
                navigate(createCommunitiesUrl(), {
                  state: { from: configurationsLandingPath },
                })
              }
              {...tid.addConfigurationButtonMobile}
            >
              Create New Configuration
            </Button>
          </div>
          <div>
            <Button onClick={handleSignOut} size="small" variant={"secondary"}>
              Logout
              <Icon name="arrow-from-left" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
